import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
} from '@/components/modules/Docs';
import {
  AgentProtocolReference,
  CHATCO_MCP_URL,
  ChatcoDisconnect,
  ConnectorCreditsTable,
  ConnectorErrors,
  ConnectorPrerequisites,
  ConnectorReferences,
  ConnectorStatusPanel,
  ConsentExplainer,
  FirstRunWalkthrough,
  getConnectorHost,
  getConnectorNeighbours,
} from '@/components/modules/Docs/connectors';

const host = getConnectorHost('codex');

const tableOfContents = [
  { id: 'availability', title: '1. Availability' },
  { id: 'before-you-start', title: '2. Before you start' },
  { id: 'connect', title: '3. Add Chat.co to Codex' },
  { id: 'approve', title: '4. Approve in Chat.co' },
  { id: 'first-answer', title: '5. Your first cited answer' },
  { id: 'credits', title: '6. Message credits' },
  { id: 'troubleshooting', title: '7. Common errors' },
  { id: 'disconnect', title: '8. Disconnect' },
  { id: 'developer-reference', title: '9. For developers & agents' },
  { id: 'references', title: '10. References' },
];

const CODEX_ADD = `codex mcp add chatco --url ${CHATCO_MCP_URL}`;
const CODEX_LOGIN = 'codex mcp login chatco';
const CODEX_CONFIG = `[mcp_servers.chatco]
url = "${CHATCO_MCP_URL}"`;
const CODEX_REMOVE = `codex mcp logout chatco
codex mcp remove chatco`;

const CodexConnectorGuide = () => (
  <>
    <NextSeo
      title="Use Chat.co experts in Codex"
      description="Add the Chat.co MCP server to OpenAI Codex with codex mcp add, sign in with OAuth, and get cited answers from your own Chat.co experts."
      canonical={`https://chat.co${host.href}`}
      openGraph={{
        url: `https://chat.co${host.href}`,
        title: 'Use Chat.co experts in Codex | Chat.co Docs',
        description: 'Add the Chat.co MCP server to Codex and get cited answers.',
        type: 'article',
        article: {
          publishedTime: '2026-09-30T00:00:00Z',
          modifiedTime: '2026-09-30T00:00:00Z',
          section: 'Integrations',
          tags: ['codex', 'openai', 'config.toml', 'connector', 'mcp', 'citations'],
        },
      }}
    />
    <DocsArticleLayout
      category="Experts in AI apps"
      categoryColor="text-orange-700"
      title="Use your Chat.co experts in Codex"
      description="Add Chat.co as a remote MCP server with two commands, then ask your experts from any Codex session."
      readingTime="7 min read"
      lastUpdated="September 2026"
      tableOfContents={tableOfContents}
      {...getConnectorNeighbours('codex')}
      keyTakeaways={[
        'Codex connects through its own MCP configuration; there is no separate directory listing.',
        'Two commands: codex mcp add, then codex mcp login.',
        'We tested discovery, a cited answer, a follow-up, a search and opening a source in Codex.',
        'The server needs no token or header in config.toml — OAuth handles sign-in.',
      ]}>
      <section id="availability" className="scroll-mt-28">
        <h2>1. Availability</h2>
        <ConnectorStatusPanel host={host} />
        <p>
          Codex adds Chat.co as a remote MCP server you configure yourself. We tested the full flow
          natively in Codex: listing experts, a cited answer, a follow-up in the same conversation,
          a direct search and opening a source. Chat.co has no separate Codex directory listing or
          approval.
        </p>
      </section>

      <section id="before-you-start" className="scroll-mt-28">
        <h2>2. Before you start</h2>
        <ConnectorPrerequisites>
          <li>
            <strong>Codex installed and signed in</strong>, as described in OpenAI&apos;s Codex
            documentation. The commands below use the Codex CLI.
          </li>
        </ConnectorPrerequisites>
      </section>

      <section id="connect" className="scroll-mt-28">
        <h2>3. Add Chat.co to Codex</h2>
        <DocsSteps>
          <DocsStep title="Add the server">
            <DocsCodeBlock code={CODEX_ADD} language="bash" />
          </DocsStep>
          <DocsStep title="Sign in">
            <DocsCodeBlock code={CODEX_LOGIN} language="bash" />
            <p>Your browser opens the Chat.co approval page. Approve it, then return to Codex.</p>
          </DocsStep>
          <DocsStep title="Check it">
            <p>
              <code>codex mcp list</code> shows chatco. Inside a Codex session, <code>/mcp</code>{' '}
              lists its four tools: <code>list_authorized_experts</code>,{' '}
              <code>consult_expert</code>, <code>search</code> and <code>fetch</code>.
            </p>
          </DocsStep>
        </DocsSteps>

        <h3 id="connect-config">Or edit config.toml</h3>
        <p>
          The same server can be declared directly in <code>~/.codex/config.toml</code>. Add the
          block below, then run <code>{CODEX_LOGIN}</code>:
        </p>
        <DocsCodeBlock code={CODEX_CONFIG} language="toml" filename="~/.codex/config.toml" />
        <p>
          In the Codex IDE extension and the ChatGPT desktop app, you can also add the server from{' '}
          <strong>Settings → MCP servers</strong>, using the same URL.
        </p>
        <DocsCallout variant="warning" title="No token settings">
          Leave <code>bearer_token_env_var</code> and <code>http_headers</code> out. Chat.co uses
          OAuth sign-in, not a token you paste or store.
        </DocsCallout>
      </section>

      <section id="approve" className="scroll-mt-28">
        <h2>4. Approve in Chat.co</h2>
        <ConsentExplainer />
      </section>

      <section id="first-answer" className="scroll-mt-28">
        <h2>5. Your first cited answer</h2>
        <FirstRunWalkthrough where="a Codex session" />
      </section>

      <section id="credits" className="scroll-mt-28">
        <h2>6. Message credits</h2>
        <ConnectorCreditsTable />
      </section>

      <section id="troubleshooting" className="scroll-mt-28">
        <h2>7. Common errors</h2>
        <ConnectorErrors
          hostRows={[
            [
              'codex mcp add does not accept --url',
              'Your Codex is older than remote-server support. Update Codex, then run the command again.',
            ],
            [
              'Tool calls fail with an authentication error',
              'Run codex mcp login chatco again. The sign-in may have been revoked in Chat.co.',
            ],
            [
              'Codex answers without using Chat.co',
              'Name it in the request: “Use my Chat.co experts to…”. Check /mcp shows chatco as enabled.',
            ],
          ]}
        />
      </section>

      <section id="disconnect" className="scroll-mt-28">
        <h2>8. Disconnect</h2>
        <ChatcoDisconnect>
          <p>Sign out and remove the server from Codex:</p>
          <DocsCodeBlock code={CODEX_REMOVE} language="bash" />
          <p>
            To pause it instead, set <code>enabled = false</code> in its <code>config.toml</code>{' '}
            block. <code>codex mcp --help</code> lists the commands your version supports.
          </p>
        </ChatcoDisconnect>
      </section>

      <section id="developer-reference" className="scroll-mt-28">
        <h2>9. For developers &amp; agents</h2>
        <AgentProtocolReference />
      </section>

      <section id="references" className="scroll-mt-28">
        <h2>10. References</h2>
        <ConnectorReferences
          vendorLinks={[
            {
              label: 'OpenAI: Model Context Protocol in Codex',
              href: 'https://developers.openai.com/codex/mcp',
            },
          ]}
        />
      </section>
    </DocsArticleLayout>
  </>
);

export default CodexConnectorGuide;
